import { OptionallyVisible } from 'components';
import { useTranslation } from 'react-i18next';

import { Panel } from 'components/containers';

import { CryptoAsset, CryptoPayment } from 'types/pwcOrder';

import { formatDateLocal } from 'utils/date';
import { capitalizeFirstLetter } from 'utils/string';

import { KEYS, LABELS } from '../../keys';
import { OrderPwcSectionProps } from '../../types';
import { Item, ItemName, ItemValue, OnChainTransactionHash, SectionWrapper } from '../styles';
import { StatusBadge } from './components/statusBadge/statusBadge';
import { CurrencyIcon, CurrencyWrapper } from './styles';

export const PaymentSection = ({ orderData, rowId, isS4cUser }: OrderPwcSectionProps) => {
  const { t } = useTranslation();

  const cryptoPayment = orderData?.charges.find((charge) => charge.uuid === rowId)?.cryptoPayment as CryptoPayment;
  const cryptoAsset = orderData?.charges.find((charge) => charge.uuid === rowId)?.cryptoAsset as CryptoAsset;

  return (
    <Panel label={t(LABELS.SECTIONS.PAYMENT.TITLE)}>
      <SectionWrapper>
        <Item>
          <ItemName>{t(LABELS.SECTIONS.PAYMENT.CRYPTOCURRENCY)}</ItemName>
          <CurrencyWrapper>
            <OptionallyVisible visible={Boolean(cryptoAsset?.iconUrl)}>
              <CurrencyIcon src={cryptoAsset?.iconUrl} alt={t(LABELS.SECTIONS.PAYMENT.CRYPTOCURRENCY)} />
            </OptionallyVisible>
            <ItemValue>{cryptoAsset?.cryptoCurrencyCode || t(KEYS.DASH)}</ItemValue>
          </CurrencyWrapper>
        </Item>

        <Item>
          <ItemName>{t(LABELS.SECTIONS.PAYMENT.NETWORK)}</ItemName>
          <ItemValue>{cryptoPayment.network ? capitalizeFirstLetter(cryptoPayment.network) : t(KEYS.DASH)}</ItemValue>
        </Item>
        <OptionallyVisible visible={isS4cUser}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.PAYMENT.PAYMENT_PROVIDER)}</ItemName>
            <ItemValue>{cryptoPayment.paymentProvider ? capitalizeFirstLetter(cryptoPayment.paymentProvider) : t(KEYS.DASH)}</ItemValue>
          </Item>

          <Item>
            <ItemName>{t(LABELS.SECTIONS.PAYMENT.PAYMENT_PROVIDER_ID)}</ItemName>
            <ItemValue>{cryptoPayment.paymentProviderId ? cryptoPayment.paymentProviderId : t(KEYS.DASH)}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(cryptoPayment.paymentProviderConnectionType)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.PAYMENT.PAYMENT_CONNECTION_TYPE)}</ItemName>
            <ItemValue>{cryptoPayment.paymentProviderConnectionType}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(cryptoPayment.paymentProviderConnectionName)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.PAYMENT.PAYMENT_CONNECTION_NAME)}</ItemName>
            <ItemValue>{cryptoPayment.paymentProviderConnectionName}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(cryptoPayment.paymentProviderStatus)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.PAYMENT.PAYMENT_STATUS)}</ItemName>
            <ItemValue>
              {cryptoPayment.paymentProviderStatus ? <StatusBadge status={cryptoPayment.paymentProviderStatus} /> : t(KEYS.DASH)}
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(cryptoPayment.paymentProviderStatusUpdatedAt)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.PAYMENT.PAYMENT_SUBMITTED_AT)}</ItemName>
            <ItemValue>{formatDateLocal(cryptoPayment.paymentProviderStatusUpdatedAt)}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(cryptoPayment.fromWalletAddress)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.PAYMENT.DEPOSIT_ADDRESS)}</ItemName>
            <ItemValue>{cryptoPayment.fromWalletAddress}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(cryptoPayment.onChain?.transactionHash)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.PAYMENT.TRANSACTION_HASH)}</ItemName>
            <OptionallyVisible visible={Boolean(cryptoPayment.onChain?.explorerUrl)}>
              <OnChainTransactionHash displayFullHash>
                <a href={cryptoPayment.onChain?.explorerUrl} target="_blank">
                  {cryptoPayment.onChain?.transactionHash}
                </a>
              </OnChainTransactionHash>
            </OptionallyVisible>
          </Item>
        </OptionallyVisible>
      </SectionWrapper>
    </Panel>
  );
};
